// Miyoneko Kawaii Theme JavaScript

class CartDrawer {
  constructor() {
    this.drawer = document.getElementById('cart-drawer');
    this.overlay = document.getElementById('cart-drawer-overlay');
    this.closeBtn = document.getElementById('cart-drawer-close');
    this.continueBtn = document.getElementById('cart-drawer-continue');
    this.itemsContainer = document.getElementById('cart-drawer-items');
    this.footer = document.getElementById('cart-drawer-footer');
    this.countEl = document.getElementById('cart-drawer-count');
    this.subtotalEl = document.getElementById('cart-drawer-subtotal');

    if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
    if (this.overlay) this.overlay.addEventListener('click', () => this.close());
    if (this.continueBtn) this.continueBtn.addEventListener('click', () => this.close());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    });
  }

  open() {
    if (this.drawer) this.drawer.classList.add('is-open');
    if (this.overlay) this.overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  close() {
    if (this.drawer) this.drawer.classList.remove('is-open');
    if (this.overlay) this.overlay.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  updateShippingBar(totalCents) {
    const threshold = 5000; // $50.00 in cents
    const bar = document.getElementById('cart-drawer-shipping');
    const text = document.getElementById('shipping-bar-text');
    const progress = document.getElementById('shipping-bar-progress');
    if (!bar || !text || !progress) return;

    const pct = Math.min((totalCents / threshold) * 100, 100);
    progress.style.width = pct + '%';

    if (totalCents >= threshold) {
      bar.classList.add('shipping-bar--complete');
      text.innerHTML = '🎉 You\'ve unlocked <span>FREE shipping!</span>';
    } else {
      bar.classList.remove('shipping-bar--complete');
      const remaining = this.formatMoney(threshold - totalCents);
      text.innerHTML = 'You\'re <span>' + remaining + '</span> away from free shipping!';
    }
  }

  async refresh() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.render(cart);
    } catch (error) {
      console.error('Error refreshing cart drawer:', error);
    }
  }

  render(cart) {
    if (this.countEl) this.countEl.textContent = cart.item_count;

    if (cart.item_count === 0) {
      this.itemsContainer.innerHTML = `
        <div class="cart-drawer__empty">
          <div class="cart-drawer__empty-icon">🛍️</div>
          <p>Your cart is empty</p>
        </div>`;
      if (this.footer) this.footer.style.display = 'none';
      return;
    }

    if (this.footer) this.footer.style.display = 'block';
    if (this.subtotalEl) this.subtotalEl.textContent = this.formatMoney(cart.total_price);
    this.updateShippingBar(cart.total_price);

    this.itemsContainer.innerHTML = cart.items.map(item => `
      <div class="cart-drawer-item" data-key="${item.key}">
        <div class="cart-drawer-item__image">
          ${item.image ? `<img src="${item.image.replace(/(\.[^.]+)$/, '_140x$1')}" alt="${item.title}">` : ''}
        </div>
        <div>
          <div class="cart-drawer-item__title">${item.product_title}</div>
          ${item.variant_title ? `<div class="cart-drawer-item__variant">${item.variant_title}</div>` : ''}
          <div class="cart-drawer-item__bottom">
            <span class="cart-drawer-item__price">${this.formatMoney(item.final_line_price)}</span>
            <div class="cart-drawer-item__quantity">
              <button data-key="${item.key}" data-qty="${item.quantity - 1}" class="cart-drawer-qty-btn" aria-label="Decrease quantity">−</button>
              <span>${item.quantity}</span>
              <button data-key="${item.key}" data-qty="${item.quantity + 1}" class="cart-drawer-qty-btn" aria-label="Increase quantity">+</button>
            </div>
          </div>
        </div>
      </div>
    `).join('');

    // Attach quantity button listeners
    this.itemsContainer.querySelectorAll('.cart-drawer-qty-btn').forEach(btn => {
      btn.addEventListener('click', async () => {
        const key = btn.dataset.key;
        const qty = parseInt(btn.dataset.qty, 10);
        await this.updateQuantity(key, qty);
      });
    });
  }

  async updateQuantity(key, quantity) {
    try {
      const response = await fetch(window.routes.cart_change_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      });
      const cart = await response.json();
      this.render(cart);

      // Update header cart count
      const headerCount = document.getElementById('cart-count');
      if (headerCount) {
        headerCount.textContent = cart.item_count;
        headerCount.style.display = cart.item_count > 0 ? 'flex' : 'none';
      }
    } catch (error) {
      console.error('Error updating cart quantity:', error);
    }
  }
}

class CartManager {
  constructor(cartDrawer) {
    this.cartCount = document.getElementById('cart-count');
    this.cartDrawer = cartDrawer;
    this.init();
  }

  init() {
    this.setupEventListeners();
    this.updateCartCount();
  }

  setupEventListeners() {
    // Product page forms
    document.addEventListener('submit', (e) => {
      if (e.target.classList.contains('product-form')) {
        e.preventDefault();
        this.addToCart(e.target);
      }
    });

    // Product card forms (quick add)
    document.addEventListener('submit', (e) => {
      if (e.target.classList.contains('product-card-form')) {
        e.preventDefault();
        this.addToCart(e.target);
      }
    });

    // Open cart drawer when clicking cart icon
    const cartLink = document.querySelector('.header-cart');
    if (cartLink) {
      cartLink.addEventListener('click', (e) => {
        e.preventDefault();
        this.cartDrawer.refresh();
        this.cartDrawer.open();
      });
    }
  }

  async addToCart(form) {
    const formData = new FormData(form);
    const button = form.querySelector('button[type="submit"]');
    const originalText = button.textContent;

    // Add loading state
    button.classList.add('loading');
    button.textContent = '';
    button.disabled = true;

    try {
      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        // Success state
        button.classList.remove('loading');
        button.classList.add('is-added');
        button.textContent = 'Added to bag';

        // Update cart count
        this.updateCartCount();

        // Open cart drawer
        this.cartDrawer.refresh();
        this.cartDrawer.open();

        // Reset button after 2 seconds
        setTimeout(() => {
          button.classList.remove('is-added');
          button.textContent = originalText;
          button.disabled = false;
        }, 2000);

      } else {
        throw new Error('Failed to add to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);

      // Error state
      button.classList.remove('loading');
      button.style.background = '#f44336';
      button.textContent = 'Error';

      setTimeout(() => {
        button.style.background = '';
        button.textContent = originalText;
        button.disabled = false;
      }, 2000);
    }
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      if (this.cartCount) {
        this.cartCount.textContent = cart.item_count;
        this.cartCount.style.display = cart.item_count > 0 ? 'flex' : 'none';
      }
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }
}

// Smooth scrolling for anchor links
function setupSmoothScrolling() {
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', function (e) {
      const target = document.querySelector(this.getAttribute('href'));
      if (target) {
        e.preventDefault();
        target.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    });
  });
}

// Intersection Observer for animations
function setupScrollAnimations() {
  const observerOptions = {
    threshold: 0.1,
    rootMargin: '0px 0px -30px 0px'
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        // Stagger cards based on their position in the grid
        const parent = entry.target.parentElement;
        const siblings = Array.from(parent.children);
        const index = siblings.indexOf(entry.target);
        const row = Math.floor(index / 4);
        const col = index % 4;
        entry.target.style.animationDelay = (col * 0.08 + row * 0.05) + 's';
        entry.target.classList.add('animate-in');
        observer.unobserve(entry.target);
      }
    });
  }, observerOptions);

  document.querySelectorAll('.product-card').forEach(card => {
    observer.observe(card);
  });

  // Animate other elements on scroll
  const fadeObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('animate-in');
        fadeObserver.unobserve(entry.target);
      }
    });
  }, { threshold: 0.1, rootMargin: '0px 0px -20px 0px' });

  document.querySelectorAll('.feature-item, .review-card, .category-card').forEach((el, i) => {
    el.style.opacity = '0';
    el.style.transform = 'translateY(20px)';
    el.style.transition = `opacity 0.5s ease ${i * 0.07}s, transform 0.5s ease ${i * 0.07}s`;
    fadeObserver.observe(el);
  });
}

function setupCategoryImageFallbacks() {
  document.querySelectorAll('.category-card img').forEach((img) => {
    const card = img.closest('.category-card');
    if (!card) return;

    const markMissing = () => {
      img.classList.add('is-hidden');
      card.classList.add('image-missing');
    };

    img.addEventListener('error', markMissing);

    if (img.complete && img.naturalWidth === 0) {
      markMissing();
    }
  });
}

// Back to Top Button
function setupBackToTop() {
  const btn = document.getElementById('back-to-top');
  if (!btn) return;

  window.addEventListener('scroll', () => {
    if (window.scrollY > 400) {
      btn.classList.add('is-visible');
    } else {
      btn.classList.remove('is-visible');
    }
  }, { passive: true });

  btn.addEventListener('click', () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });
}

// Initialize everything when DOM is loaded
document.addEventListener('DOMContentLoaded', function() {
  const cartDrawer = new CartDrawer();
  new CartManager(cartDrawer);
  setupSmoothScrolling();
  setupScrollAnimations();
  setupCategoryImageFallbacks();
  setupBackToTop();

  console.log('🌸 Miyoneko Kawaii Theme loaded successfully!');
});

/* --- 2026 refresh: header shadow + scroll reveal --- */
(function () {
  var header = document.querySelector('.site-header');
  if (header) {
    var onScroll = function () {
      header.classList.toggle('is-scrolled', window.scrollY > 8);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  var sel = '.section .container > *, .rd-hero-in > div, .rd-meet, .rd-how';
  var targets = [].slice.call(document.querySelectorAll(sel)).filter(function (el) {
    // never animate third-party widgets: they mount late and would stay hidden
    return !el.className || String(el.className).indexOf('jdgm') === -1;
  }).filter(function (el) {
    return !el.querySelector('[class*="jdgm"]');
  });
  if (!('IntersectionObserver' in window) || !targets.length) return;
  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
  targets.forEach(function (el) { el.classList.add('rd-reveal'); io.observe(el); });
  // safety net: nothing stays invisible, whatever happens
  setTimeout(function () {
    document.querySelectorAll('.rd-reveal:not(.is-in)').forEach(function (el) { el.classList.add('is-in'); });
  }, 1600);
})();

/* --- hero speech bubble: a different line on every visit --- */
(function () {
  var el = document.getElementById('rd-bubble');
  var lines = window.RD_BUBBLE_LINES;
  if (!el || !lines || lines.length < 2) return;
  el.textContent = lines[Math.floor(Math.random() * lines.length)];
})();

/* --- newsletter: submit without a page reload (no scroll jump) --- */
(function () {
  document.querySelectorAll('form.rd-ajax-form, .newsletter-section form, #footer-newsletter').forEach(function (form) {
    if (form.dataset.rdBound) return;
    form.dataset.rdBound = '1';
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var btn = form.querySelector('button[type=submit]');
      var wrap = form.closest('.newsletter-content') || form.parentNode;
      var old = btn ? btn.textContent : '';
      if (btn) { btn.disabled = true; btn.textContent = 'Joining…'; }
      form.querySelectorAll('.rd-note-ok,.rd-note-err').forEach(function (n) { n.remove(); });
      fetch(form.action, { method: 'POST', body: new FormData(form), headers: { 'Accept': 'text/html' } })
        .then(function (r) {
          var note = document.createElement('p');
          note.className = r.ok ? 'rd-note-ok' : 'rd-note-err';
          note.textContent = r.ok
            ? "You're on the list. Watch your inbox for the next drop."
            : 'That did not go through. Try again in a moment?';
          form.insertBefore(note, form.firstChild);
          if (r.ok) form.querySelectorAll('input[type=email]').forEach(function (i) { i.value = ''; });
        })
        .catch(function () {
          var note = document.createElement('p');
          note.className = 'rd-note-err';
          note.textContent = 'That did not go through. Try again in a moment?';
          form.insertBefore(note, form.firstChild);
        })
        .finally(function () { if (btn) { btn.disabled = false; btn.textContent = old; } });
    });
  });
})();

/* --- reviews carousel arrows --- */
(function () {
  var track = document.getElementById('rd-revs-track');
  if (!track) return;
  var wrap = track.closest('.rd-revs');
  if (!wrap) return;
  var step = function () { return Math.max(280, track.clientWidth * 0.8); };
  var prev = wrap.querySelector('.rd-revs__nav--prev');
  var next = wrap.querySelector('.rd-revs__nav--next');
  if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: 'smooth' }); });
  if (next) next.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: 'smooth' }); });
  var sync = function () {
    if (prev) prev.style.opacity = track.scrollLeft <= 4 ? '.35' : '1';
    if (next) next.style.opacity = (track.scrollLeft + track.clientWidth >= track.scrollWidth - 4) ? '.35' : '1';
  };
  track.addEventListener('scroll', sync, { passive: true });
  sync();
})();

/* --- reviews carousel: gentle auto-advance --- */
(function () {
  var track = document.getElementById('rd-revs-track');
  if (!track) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var paused = false;
  ['mouseenter', 'focusin', 'touchstart', 'pointerdown'].forEach(function (ev) {
    track.addEventListener(ev, function () { paused = true; }, { passive: true });
  });
  ['mouseleave', 'focusout'].forEach(function (ev) {
    track.addEventListener(ev, function () { paused = false; }, { passive: true });
  });

  var card = track.querySelector('.rd-rev');
  var step = function () {
    var w = card ? card.getBoundingClientRect().width : 300;
    return w + 16; // card + gap
  };

  setInterval(function () {
    if (paused || document.hidden) return;
    var atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 8;
    track.scrollTo({ left: atEnd ? 0 : track.scrollLeft + step(), behavior: 'smooth' });
  }, 3800);
})();
